import type { MemberRole } from '../../models/CurrentUserProject';

export type User = {
  avatar?: string;
  email: string;
  id: number;
  name: string;
  role?: MemberRole;
  large_avatar_url?: string;
  new_avatar_url?: string;
  firstName?: null | string;
  first_name?: null | string;
  surname?: string;
};

export type Column = {
  id: number;
  name: string;
  position: number;
  color: string;
  status_id: null;
};

export type Tag = {
  display: string;
  id: number;
  aiSuggestion?: boolean;
};

export type MentionableTask = {
  id: number;
  display: string;
  title?: string | null;
  column?: unknown;
  statusId: number | null;
  severity: number;
  requester?: string;
  columnId: number | null;
  viewURL: string;
  localTaskId: number;
};

export type Severity = 0 | 1 | 2 | 3 | 4 | undefined;

export type ShortcutConfig = {
  assignees: number[];
  canEditAssignees: boolean;
  assignableUsers: User[];
  onAssigneesChange: (assignees: number[]) => void;
  watcherIds?: number[];
  onWatchersChange?: (watcherIds: number[]) => void;
  guestVisible?: boolean;
  status?: number;
  canEditStatus: boolean;
  onStatusChange: (columnId: number) => void;
  columns: Column[];
  tags: Tag[];
  canEditTags: boolean;
  onTagsChange: (tags: Tag[]) => void;
  availableTags: Tag[];
  /** Create-task editor only. Project `create_tags_via_new_task`. */
  createTagsViaNewTask?: boolean;
  severity: Severity;
  canEditSeverity: boolean;
  onSeverityChange: (severity: Severity) => void;
  mentionableTasks: MentionableTask[];
  currentUserId?: number | string | null;
  commentSlash?: CommentSlashConfig | null;
  dueAt?: string | null;
  canEditDueDate?: boolean;
  onDueDateChange?: (dueAt: string | null) => void;
};

export type CommentSlashConfig = {
  mentionableUsers: User[];
  canTogglePrivate: boolean;
  canCreatePrivate: boolean;
  isPrivate: boolean;
  hasInvalidMentions: boolean;
  onPrivateToggle: (checked: boolean) => void;
  onPrivateUpgrade?: () => void;
};

export type SlashMenuItem = {
  title: string;
  subtext?: string;
  shortcut?: string[];
  aliases?: string[];
  group: string;
  onSelect: (props: {
    editor: import('@tiptap/core').Editor;
    range: import('@tiptap/core').Range;
  }) => void;
  icon?: React.ReactNode;
  subItems?: SlashMenuItem[];
  slashTaskRow?: { severity: number; localTaskId: number; labelPlain: string };
  slashUserRow?: User;
  slashPeopleAction?: 'assign' | 'cc';
  slashSeverityRow?: { priority: number };
  slashStatusRow?: boolean;
  slashTagRow?: { create: boolean };
};

export type MentionPreviewUser = { id: number; name: string; email?: string };

export type EmojiPickerConfig = {
  getContainer: () => HTMLDivElement;
  apiDomain: string;
  isAdminView: boolean;
  projectId: number;
};

export type MarkdownMentionPreviewContext = {
  descriptionMarkdown: string;
  projectMembers?: MentionPreviewUser[];
  projectUsers?: MentionPreviewUser[];
  allUsers?: MentionPreviewUser[];
  includeGuests?: boolean;
  mentionableTasks?: MentionableTask[];
  setActiveTask?: (taskId: number) => void;
  bugherdUrl?: string;
  projectId?: number;
  isComment?: boolean;
  isPrivate?: boolean;
};

export function resolveMentionPreviewContext(
  ctx: MarkdownMentionPreviewContext | undefined | null
): MarkdownMentionPreviewContext | undefined {
  if (ctx == null) return undefined;
  const { includeGuests, projectUsers: pu, projectMembers } = ctx;
  const projectUsers = includeGuests && pu?.length ? pu : projectMembers;
  return { ...ctx, projectUsers };
}

/** Bundles props passed from task-details / similar shells into {@link TaskDescriptionField}. */
export type TaskDescriptionRichTextBundle = {
  shortcutConfig?: ShortcutConfig | null;
  mentionPreviewContext?: MarkdownMentionPreviewContext;
};
